import React from 'react';

/* ============================================================================
 * Material 3 primitive components (GroVision admin)
 * Ported from the Claude Design prototype (m3-components.jsx) to TSX.
 * All styling is inline, keyed off the M3 / GroVision CSS custom properties.
 * ========================================================================= */

type CSS = React.CSSProperties;

/* ---- Icon (Material Symbols Rounded) ----------------------------------- */
export interface SymProps {
  name: string;
  size?: number;
  fill?: 0 | 1;
  weight?: number;
  grade?: number;
  className?: string;
  style?: CSS;
}
export function Sym({
  name,
  size = 24,
  fill = 0,
  weight = 400,
  grade = 0,
  className,
  style,
}: SymProps): React.ReactElement {
  return (
    <span
      className={`material-symbols-rounded${className ? ` ${className}` : ''}`}
      style={{
        fontSize: size,
        fontVariationSettings: `'FILL' ${fill}, 'wght' ${weight}, 'GRAD' ${grade}, 'opsz' ${size}`,
        ...style,
      }}
    >
      {name}
    </span>
  );
}

/* ---- IconButton -------------------------------------------------------- */
export interface IconButtonProps {
  icon: string;
  onClick?: (e: React.MouseEvent) => void;
  fill?: 0 | 1;
  color?: string;
  size?: number;
  title?: string;
  disabled?: boolean;
  style?: CSS;
}
export function IconButton({
  icon,
  onClick,
  fill = 0,
  color = 'var(--md-sys-color-on-surface-variant)',
  size = 24,
  title,
  disabled,
  style,
}: IconButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 40,
        height: 40,
        borderRadius: 9999,
        border: 'none',
        background: 'transparent',
        color,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: 'background 150ms',
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = 'rgba(73,69,79,.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <Sym name={icon} size={size} fill={fill} />
    </button>
  );
}

/* ---- Button ------------------------------------------------------------ */
export type ButtonVariant = 'filled' | 'tonal' | 'elevated' | 'outlined' | 'text';
export interface ButtonProps {
  variant?: ButtonVariant;
  icon?: string;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  style?: CSS;
}
const BTN_VARIANT: Record<ButtonVariant, CSS> = {
  filled: { background: 'var(--md-sys-color-primary)', color: 'var(--md-sys-color-on-primary)' },
  tonal: {
    background: 'var(--md-sys-color-secondary-container)',
    color: 'var(--md-sys-color-on-secondary-container)',
  },
  elevated: {
    background: 'var(--md-sys-color-surface-container-low)',
    color: 'var(--md-sys-color-primary)',
    boxShadow: 'var(--md-sys-elevation-1)',
  },
  outlined: {
    background: 'transparent',
    color: 'var(--md-sys-color-primary)',
    border: '1px solid var(--md-sys-color-outline)',
  },
  text: { background: 'transparent', color: 'var(--md-sys-color-primary)' },
};
export function Button({
  variant = 'filled',
  icon,
  children,
  onClick,
  disabled,
  type = 'button',
  style,
}: ButtonProps): React.ReactElement {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 40,
        padding: icon ? '0 18px 0 14px' : '0 22px',
        borderRadius: 9999,
        border: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        font: '500 14px/20px var(--gs-font-kr)',
        cursor: disabled ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
        transition: 'filter 150ms, background 150ms',
        ...BTN_VARIANT[variant],
        ...(disabled
          ? {
              background: 'var(--md-sys-color-surface-container-high)',
              color: 'var(--md-sys-color-on-surface-variant)',
              border: 'none',
              boxShadow: 'none',
              opacity: 0.65,
            }
          : {}),
        ...style,
      }}
    >
      {icon && <Sym name={icon} size={18} />}
      {children}
    </button>
  );
}

/* ---- Chip (filter / assist) -------------------------------------------- */
export interface ChipProps {
  icon?: string;
  selected?: boolean;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  style?: CSS;
}
export function Chip({ icon, selected, children, onClick, style }: ChipProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 32,
        padding: '0 12px',
        borderRadius: 8,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        font: `${selected ? 600 : 500} 13px/1 var(--gs-font-kr)`,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        background: selected ? 'var(--md-sys-color-secondary-container)' : 'transparent',
        color: selected
          ? 'var(--md-sys-color-on-secondary-container)'
          : 'var(--md-sys-color-on-surface-variant)',
        border: selected ? 'none' : '1px solid var(--md-sys-color-outline-variant)',
        transition: 'background 150ms',
        ...style,
      }}
    >
      {selected && <Sym name="check" size={16} />}
      {icon && !selected && <Sym name={icon} size={16} />}
      {children}
    </button>
  );
}

/* ---- Card -------------------------------------------------------------- */
export type CardVariant = 'elevated' | 'filled' | 'outlined';
export interface CardProps {
  variant?: CardVariant;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  style?: CSS;
}
const CARD_VARIANT: Record<CardVariant, CSS> = {
  elevated: {
    background: 'var(--md-sys-color-surface-container-low)',
    boxShadow: 'var(--md-sys-elevation-1)',
  },
  filled: { background: 'var(--md-sys-color-surface-container-highest)' },
  outlined: {
    background: 'var(--md-sys-color-surface)',
    border: '1px solid var(--md-sys-color-outline-variant)',
  },
};
export function Card({
  variant = 'elevated',
  children,
  onClick,
  className,
  style,
}: CardProps): React.ReactElement {
  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        borderRadius: 12,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : undefined,
        ...CARD_VARIANT[variant],
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ---- Avatar ------------------------------------------------------------ */
export interface AvatarProps {
  src?: string;
  name?: string;
  size?: number;
  color?: string;
  fg?: string;
  style?: CSS;
}
export function Avatar({
  src,
  name = '',
  size = 40,
  color = 'var(--md-sys-color-primary-container)',
  fg = 'var(--md-sys-color-on-primary-container)',
  style,
}: AvatarProps): React.ReactElement {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('');
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 9999,
        flexShrink: 0,
        background: src ? `center/cover no-repeat url(${src})` : color,
        color: fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        font: `500 ${Math.round(size * 0.4)}px/1 var(--gs-font-kr)`,
        ...style,
      }}
    >
      {!src && initials}
    </div>
  );
}

/* ---- Divider ----------------------------------------------------------- */
export function Divider({ inset = 0, style }: { inset?: number; style?: CSS }): React.ReactElement {
  return (
    <div
      style={{
        height: 1,
        background: 'var(--md-sys-color-outline-variant)',
        marginLeft: inset,
        ...style,
      }}
    />
  );
}

/* ---- SearchBar --------------------------------------------------------- */
export interface SearchBarProps {
  placeholder?: string;
  value?: string;
  onChange?: (v: string) => void;
  width?: number | string;
  style?: CSS;
}
export function SearchBar({
  placeholder = '검색',
  value,
  onChange,
  width = 280,
  style,
}: SearchBarProps): React.ReactElement {
  return (
    <div
      style={{
        width,
        height: 42,
        borderRadius: 9999,
        background: 'var(--md-sys-color-surface-container-high)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 16px',
        ...style,
      }}
    >
      <Sym name="search" size={20} style={{ color: 'var(--md-sys-color-on-surface-variant)' }} />
      <input
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          font: '400 14px/20px var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface)',
        }}
      />
    </div>
  );
}

/* ---- Switch ------------------------------------------------------------ */
export interface SwitchProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  style?: CSS;
}
export function Switch({ checked, onChange, style }: SwitchProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{
        width: 46,
        height: 28,
        borderRadius: 9999,
        border: checked ? 'none' : '2px solid var(--md-sys-color-outline)',
        background: checked ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-surface-container-highest)',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 150ms',
        flexShrink: 0,
        ...style,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '50%',
          left: checked ? 24 : 4,
          transform: 'translateY(-50%)',
          width: checked ? 20 : 16,
          height: checked ? 20 : 16,
          borderRadius: 9999,
          background: checked ? 'var(--md-sys-color-on-primary)' : 'var(--md-sys-color-outline)',
          transition: 'left 150ms, width 150ms, height 150ms',
        }}
      />
    </button>
  );
}

/* ---- Stepper (numeric) ------------------------------------------------- */
export interface StepperProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
  style?: CSS;
}
export function Stepper({ value, min = 1, max = 12, onChange, style }: StepperProps): React.ReactElement {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 36,
        borderRadius: 9999,
        border: '1px solid var(--md-sys-color-outline-variant)',
        overflow: 'hidden',
        ...style,
      }}
    >
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        style={stepBtn}
      >
        <Sym name="remove" size={18} />
      </button>
      <span
        className="gs-num"
        style={{ minWidth: 28, textAlign: 'center', font: '600 14px/1 var(--gs-font-kr)' }}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        style={stepBtn}
      >
        <Sym name="add" size={18} />
      </button>
    </div>
  );
}
const stepBtn: CSS = {
  width: 36,
  height: 36,
  border: 'none',
  background: 'transparent',
  color: 'var(--md-sys-color-on-surface-variant)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};
